import { edgeGeom, salidasDe, etiquetaPropia } from "./flowGeometry";
import { EDGE_CLR, EDGE_CLR_EST, CLASE_POR_ESTADO } from "../../constants";

// El COLOR de una arista dice de que rama sale; el TRAZO, si es opcional. Son
// dos canales distintos y antes iban por el mismo: una clase que decidia las
// dos cosas, con `opcional` por delante de la rama. El resultado era que la
// rama opcional de un condicional salia violeta y perdia su identidad justo
// donde mas importa —de un rombo salen dos caminos y hay que saber cual es
// cual—. Ahora la rama gana el color y lo opcional se dice con el guionado.
// Una flecha normal se pinta del color del paso AL QUE APUNTA: la regla mas
// corta que se puede dar —"la flecha es del color de lo que señala"— y por eso
// no hay que explicarla. El rastro de color acaba justo donde esta el trabajo.
//
// Se probo primero al reves, por el paso del que SALE, y engañaba: terminar el
// primero pintaba de verde todo lo que salia de el, o sea el camino que aun no
// se ha recorrido.
//
// Las ramas con identidad —si, no, opcional, bucle— conservan su color: de un
// rombo salen dos caminos y saber cual es cual pesa mas que saber como va el
// paso que hay al final.
export function claseArista(e, { seleccionada, ciclo, estadoDestino } = {}) {
  if (seleccionada) return "sel";
  if (ciclo) return "retro";
  if (e.from_handle === "si") return "si";
  if (e.from_handle === "no") return "no";
  if (e.opcional) return "opt";
  return CLASE_POR_ESTADO[estadoDestino] || "normal";
}

// Un retorno se guiona corto y se lleva la preferencia: que la arista cierre un
// bucle es mas raro y mas importante que que sea opcional.
export const trazoArista = (e, clase) =>
  clase === "retro" ? "3 4" : e.opcional ? "7 5" : undefined;

export default function FlowEdge({
  e, nodos, seleccionada, ciclo, atenuada, editable,
  encima, intercalar, estadoDestino, onEntrar, onSalir, onSeleccionar, onBorrar,
}) {
  const g = edgeGeom(e, nodos);
  if (!g) return null;

  const clase = claseArista(e, { seleccionada, ciclo, estadoDestino });
  const color = EDGE_CLR[clase] || EDGE_CLR_EST[clase];
  // La etiqueta, por orden: la puesta a mano en la arista, el nombre de la
  // salida leido del NODO de origen (asi renombrarla actualiza todas sus
  // flechas), y el si/no de un condicional.
  const origen = nodos[String(e.from_task_id)];
  const nombreSalida = origen?.kind === "switch"
    ? salidasDe(origen).find(x => x.clave === e.from_handle)?.nombre
    : null;
  // El "Si" y el "No" YA NO se rotulan sobre la flecha: lo dice el color, verde
  // y rojo, desde el propio puerto del que sale. Dos etiquetas por cada rombo
  // era mucho texto para un dato binario que el color da de un vistazo.
  //
  // Va por etiquetaPropia y no por e.etiqueta a secas porque la palabra esta
  // GUARDADA: quitar solo el rotulo derivado dejaba las flechas que ya existian
  // rotuladas igual. Una etiqueta de verdad —"si aprueban", "si pasa de 5.000"—
  // si se pinta: esa la ha escrito una persona y dice algo que el color no.
  //
  // El dato sigue en el <title> de abajo, que es donde no molesta y donde lo
  // encuentra quien lo necesite.
  const etiqueta = etiquetaPropia(e) || nombreSalida || null;

  // DM Mono es monoespaciada, asi que el ancho se estima sin medir el texto.
  const anchoLbl = etiqueta ? etiqueta.length * 6.1 + 12 : 0;

  // Un <title> de SVG solo admite UN nodo de texto: si se le pasan varios hijos,
  // el navegador pinta el marcado como texto dentro del tooltip. Se compone la
  // cadena antes en lugar de interpolar varios trozos en el JSX.
  const tooltip = [
    [nodos[String(e.from_task_id)]?.title, nodos[String(e.to_task_id)]?.title]
      .filter(Boolean).join(" → "),
    etiqueta || (e.from_handle === "si" ? "rama del si"
      : e.from_handle === "no" ? "rama del no" : null),
    e.opcional ? "rama opcional" : null,
    ciclo ? "cierra un bucle" : null,
    // El color se dice tambien con palabras: en las lineas compiten ya varios
    // colores y un color sin nombre no lo lee quien no distingue bien los tonos.
    clase === CLASE_POR_ESTADO[estadoDestino]
      ? `lleva a un paso ${String(estadoDestino).toLowerCase()}` : null,
  ].filter(Boolean).join(" · ");

  // El boton de borrar aparece al pasar por encima, en el punto medio. Es lo
  // que hace que quitar una conexion sea un gesto y no una expedicion: antes
  // habia que seleccionarla, encontrar el panel y pulsar alli.
  const mostrarBorrar = editable && (encima || seleccionada);

  return (
    <g opacity={atenuada ? 0.28 : 1}
      onPointerEnter={() => onEntrar?.(e)}
      onPointerLeave={() => onSalir?.(e)}>
      <title>{tooltip}</title>

      {/* Gemelo invisible y ancho: un trazo de 1,8 px es imposible de pulsar */}
      <path className="df-edge-hit" d={g.d}
        onPointerDown={ev => { ev.stopPropagation(); onSeleccionar?.(e); }} />

      {/* Resalte de "suelta aqui y te intercalas". Va DEBAJO de la arista y no
          en su lugar: la flecha conserva su color de rama —que es lo que dice a
          donde lleva— y el halo dice que hay un hueco donde entrar. Sustituirle
          el color habria perdido justo el dato que hace falta para decidir. */}
      {intercalar && (
        <path className="df-edge-hueco" d={g.d} fill="none" pointerEvents="none" />
      )}

      <path className="df-edge" d={g.d} fill="none" stroke={color}
        strokeWidth={seleccionada ? 3 : 1.8} strokeDasharray={trazoArista(e, clase)}
        markerEnd={`url(#df-arrow-${clase})`} pointerEvents="none" />

      {/* La etiqueta se aparta hacia arriba cuando sale el boton, para no
          quedar una encima de la otra. */}
      {etiqueta && (
        <g pointerEvents="none">
          <rect x={g.mid.x - anchoLbl / 2} y={g.mid.y - 8 - (mostrarBorrar ? 15 : 0)}
            width={anchoLbl} height={16} rx={8}
            fill="var(--surface)" stroke={color} strokeWidth={1} />
          <text className="df-lbl" x={g.mid.x} y={g.mid.y - (mostrarBorrar ? 15 : 0)}
            style={{ fill: color }}>
            {etiqueta}
          </text>
        </g>
      )}

      {mostrarBorrar && (
        <g className="df-edge-del" onPointerDown={ev => { ev.stopPropagation(); onBorrar?.(e); }}>
          <title>Eliminar esta conexion</title>
          {/* Circulo de 11 de radio: el area de pulsacion tiene que ser comoda
              incluso con el dedo, aunque el icono dibujado sea mas pequeño. */}
          <circle cx={g.mid.x} cy={g.mid.y} r={11} fill="var(--surface)"
            stroke="var(--red)" strokeWidth={1.5} />
          <path d={`M${g.mid.x - 3.5},${g.mid.y - 3.5} L${g.mid.x + 3.5},${g.mid.y + 3.5}
                    M${g.mid.x + 3.5},${g.mid.y - 3.5} L${g.mid.x - 3.5},${g.mid.y + 3.5}`}
            stroke="var(--red)" strokeWidth={1.8} strokeLinecap="round" fill="none" />
        </g>
      )}
    </g>
  );
}
